import React, { PropsWithChildren, useContext } from 'react';
import Head from 'next/head';

import { MetaData } from 'data/meta.json';
import { UrlContext } from 'modules/seo/context/UrlContext';
import { Images } from 'theme/images';

/**
 * Renders page head
 *
 * @param props - Page props
 */
export function PageHead(props: PageHeadProps) {
  const { title, description, image, canonicalUrl: canonicalUrlOverride } = props;
  const { canonicalUrl: nextContextCanonicalUrl } = useContext(UrlContext);
  const ogImage = image || `${Config.origin}${Images.sharedImage}`;
  const isFirebase = Config.buildPlatform === 'firebase';
  const isDev = Config.Shared.STAGE === 'dev';
  const prohibitIndexing = isDev && isFirebase;
  const actualCanonicalUrl = canonicalUrlOverride ?? nextContextCanonicalUrl;

  return (
    <Head>
      <title>{title}</title>
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@NakedCover_SA" />
      {prohibitIndexing && <meta name="robots" content="noindex" />}
      {actualCanonicalUrl && <link rel="canonical" href={actualCanonicalUrl} />}
      {actualCanonicalUrl && <meta property="og:url" content={actualCanonicalUrl} />}
      {description && <meta name="description" content={description} />}
      {description && <meta property="og:description" content={description} />}
      {title && <meta property="og:title" content={title} />}
    </Head>
  );
}
/**
 * Adds meta tags to page
 *
 * @param props - Render props
 */
export function Page(props: PageProps) {
  const { name, children, ...otherProps } = props;
  const { title, description } = (MetaData[name] as Record<string, string>) || {};

  return (
    <>
      <PageHead title={title} description={description} {...otherProps} />
      {children}
    </>
  );
}
type Name = keyof typeof MetaData;

export interface PageHeadProps {
  /** Page url */
  canonicalUrl?: string;
  /** Page meta data and og graph description */
  description?: string;
  itemType?: 'car'; // let's dump this into query
  /** Title of the page */
  title: string;
  /** Open graph image */
  image?: string;
  /** Page Query information. */
  query?: Record<string, string | string[]>;
}

/* Type definitions */
export interface PageProps extends PropsWithChildren<Partial<PageHeadProps>> {
  /** Name of the page to load meta data for */
  name?: Name;
  /** Background color of the footer's rounded corners on mobile */
  cornerColorMobile?: string;
  /** Background color of the footer's rounded corners on desktop */
  cornerColorDesktop?: string;
}
